import { useEffect, useMemo, useState } from 'react';
import { Plus, Send, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useApiToken } from '@/auth/hooks';
import type {
  ClarifyPriority,
  ClarifyTitleSuggestionState,
  CreateClarifyRequestInput,
} from '@/types/clarify';
import type { DocumentDto } from '@/types/document';

interface NewRequestDialogProps {
  onSubmit: (input: CreateClarifyRequestInput) => Promise<void>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  prefill?: Partial<CreateClarifyRequestInput>;
  requestKey?: string;
  suggestedTitle?: string;
  titleSuggestionState?: ClarifyTitleSuggestionState;
  suggestedBusinessContext?: string;
  businessContextSuggestionState?: ClarifyTitleSuggestionState;
  hideTrigger?: boolean;
}

const PRIORITIES: ClarifyPriority[] = ['critical', 'high', 'medium', 'low'];

function buildInitialDraft(prefill?: Partial<CreateClarifyRequestInput>) {
  return {
    title: prefill?.title ?? '',
    question: prefill?.question ?? '',
    businessContext: prefill?.businessContext ?? '',
    tags: prefill?.tags ?? [],
    linkedDocumentIds: prefill?.linkedDocumentIds ?? [],
    priority: prefill?.priority ?? 'medium',
  } satisfies CreateClarifyRequestInput;
}

export function NewRequestDialog({
  onSubmit,
  open,
  onOpenChange,
  prefill,
  requestKey,
  suggestedTitle,
  titleSuggestionState = 'idle',
  suggestedBusinessContext,
  businessContextSuggestionState = 'idle',
  hideTrigger = false,
}: NewRequestDialogProps) {
  const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
  const { getAccessToken } = useApiToken();
  const [internalOpen, setInternalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [businessContext, setBusinessContext] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [linkedDocumentIds, setLinkedDocumentIds] = useState<string[]>([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [availableDocuments, setAvailableDocuments] = useState<DocumentDto[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [priority, setPriority] = useState<ClarifyPriority>('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [titleDirty, setTitleDirty] = useState(false);
  const [businessContextDirty, setBusinessContextDirty] = useState(false);

  const resolvedOpen = open ?? internalOpen;

  const setDialogOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      setSubmitError(null);
    }
  };

  useEffect(() => {
    if (!resolvedOpen) {
      return;
    }

    const draft = buildInitialDraft(prefill);
    setTitle(draft.title);
    setQuestion(draft.question);
    setBusinessContext(draft.businessContext ?? '');
    setTags(draft.tags);
    setLinkedDocumentIds(draft.linkedDocumentIds ?? []);
    setPriority(draft.priority);
    setTagInput('');
    setDocumentSearch('');
    setSubmitError(null);
    setTitleDirty(false);
    setBusinessContextDirty(false);
  }, [prefill, requestKey, resolvedOpen]);

  useEffect(() => {
    if (!resolvedOpen || !suggestedTitle || titleDirty) {
      return;
    }

    setTitle(suggestedTitle);
  }, [resolvedOpen, suggestedTitle, titleDirty]);

  useEffect(() => {
    if (!resolvedOpen || !suggestedBusinessContext || businessContextDirty) {
      return;
    }

    setBusinessContext(suggestedBusinessContext);
  }, [businessContextDirty, resolvedOpen, suggestedBusinessContext]);

  useEffect(() => {
    if (!resolvedOpen || !activeWorkspace) {
      return;
    }

    let cancelled = false;
    const loadDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const token = await getAccessToken().catch(() => null);
        const response = await fetch(
          `/api/dotnet/documents?workspaceId=${activeWorkspace.id}&page=1&pageSize=50`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!response.ok) {
          throw new Error('Dokumente konnten nicht geladen werden.');
        }

        const data = (await response.json()) as { items?: DocumentDto[] } | DocumentDto[];
        const items = Array.isArray(data) ? data : (data.items ?? []);
        if (!cancelled) {
          setAvailableDocuments(items);
        }
      } catch {
        if (!cancelled) {
          setAvailableDocuments([]);
        }
      } finally {
        if (!cancelled) {
          setDocumentsLoading(false);
        }
      }
    };

    void loadDocuments();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspace, getAccessToken, resolvedOpen]);

  const validation = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = 'Titel ist erforderlich.';
    if (!question.trim()) errors.question = 'Fachliche Frage ist erforderlich.';
    if (title.trim().length > 200) errors.title = 'Maximal 200 Zeichen.';
    if (question.trim().length > 2000) errors.question = 'Maximal 2000 Zeichen.';
    if (businessContext.trim().length > 3000) errors.businessContext = 'Maximal 3000 Zeichen.';
    return errors;
  }, [businessContext, question, title]);

  const reset = () => {
    const draft = buildInitialDraft(prefill);
    setTitle(draft.title);
    setQuestion(draft.question);
    setBusinessContext(draft.businessContext ?? '');
    setTagInput('');
    setTags(draft.tags);
    setLinkedDocumentIds(draft.linkedDocumentIds ?? []);
    setDocumentSearch('');
    setPriority(draft.priority);
    setSubmitError(null);
    setTitleDirty(false);
    setBusinessContextDirty(false);
  };

  const filteredDocuments = useMemo(() => {
    const query = documentSearch.trim().toLowerCase();
    if (!query) {
      return availableDocuments;
    }

    return availableDocuments.filter((document) => {
      const fileName = document.fileName.toLowerCase();
      const displayTitle = document.displayTitle?.toLowerCase() ?? '';
      return fileName.includes(query) || displayTitle.includes(query);
    });
  }, [availableDocuments, documentSearch]);

  const addTag = () => {
    const next = tagInput.trim();
    if (!next || tags.includes(next) || tags.length >= 10) return;
    setTags((current) => [...current, next]);
    setTagInput('');
  };

  const removeTag = (tag: string) => setTags((current) => current.filter((entry) => entry !== tag));

  const toggleLinkedDocument = (documentId: string) => {
    setLinkedDocumentIds((current) => (
      current.includes(documentId)
        ? current.filter((entry) => entry !== documentId)
        : [...current, documentId]
    ));
  };

  const handleSubmit = async () => {
    if (Object.keys(validation).length > 0) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit({
        title: title.trim(),
        question: question.trim(),
        businessContext: businessContext.trim(),
        tags,
        linkedDocumentIds,
        priority,
      });
      reset();
      setDialogOpen(false);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Request konnte nicht eingereicht werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const titleSuggestionMessage =
    titleSuggestionState === 'loading'
      ? 'Titelvorschlag wird geladen. Du kannst den Request bereits mit dem Fallback-Titel einreichen.'
      : titleSuggestionState === 'ready'
        ? 'LLM-Titelvorschlag übernommen.'
        : titleSuggestionState === 'fallback'
          ? 'LLM-Titel nicht verfügbar. Es wird ein Fallback-Titel verwendet.'
          : null;

  const titleSuggestionClassName =
    titleSuggestionState === 'fallback'
      ? 'text-amber-600 dark:text-amber-300'
      : titleSuggestionState === 'ready'
        ? 'text-emerald-600 dark:text-emerald-300'
        : 'text-muted-foreground';

  const businessContextSuggestionMessage =
    businessContextSuggestionState === 'loading'
      ? 'Business-Kontext wird aus dem Chat vorbereitet. Du kannst ihn bei Bedarf bereits selbst ergänzen.'
      : businessContextSuggestionState === 'ready'
        ? 'LLM-Kontextvorschlag übernommen.'
        : businessContextSuggestionState === 'fallback'
          ? 'LLM-Kontext nicht verfügbar. Es wird der Chat-Kontext als Fallback verwendet.'
          : null;

  const businessContextSuggestionClassName =
    businessContextSuggestionState === 'fallback'
      ? 'text-amber-600 dark:text-amber-300'
      : businessContextSuggestionState === 'ready'
        ? 'text-emerald-600 dark:text-emerald-300'
        : 'text-muted-foreground';

  return (
    <Dialog open={resolvedOpen} onOpenChange={setDialogOpen}>
      {!hideTrigger ? (
        <DialogTrigger asChild>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Neuer Knowledge Request
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="flex max-h-[90dvh] w-[min(56rem,calc(100vw-1.5rem))] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border/60 px-5 pb-4 pt-5 sm:px-6">
          <DialogTitle>Neuen Knowledge Request erstellen</DialogTitle>
          <DialogDescription>
            Erfasse die fachliche Frage, ordne Prioritaet und Kontext zu und reiche den Request für Triage und Experteninterview ein.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="space-y-2">
            <Label htmlFor="clarify-title">Titel</Label>
            <Input
              id="clarify-title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                setTitleDirty(true);
              }}
              maxLength={200}
            />
            {titleSuggestionMessage ? (
              <p className={`text-xs ${titleSuggestionClassName}`}>{titleSuggestionMessage}</p>
            ) : null}
            {validation.title ? <p className="text-xs text-destructive">{validation.title}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="clarify-question">Fachliche Frage</Label>
            <Textarea id="clarify-question" value={question} onChange={(event) => setQuestion(event.target.value)} className="min-h-[120px]" maxLength={2000} />
            {validation.question ? <p className="text-xs text-destructive">{validation.question}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="clarify-context">Business-Kontext</Label>
            <Textarea
              id="clarify-context"
              value={businessContext}
              onChange={(event) => {
                setBusinessContext(event.target.value);
                setBusinessContextDirty(true);
              }}
              className="min-h-[144px] text-sm leading-6"
              maxLength={3000}
            />
            {businessContextSuggestionMessage ? (
              <p className={`text-xs ${businessContextSuggestionClassName}`}>{businessContextSuggestionMessage}</p>
            ) : null}
            {validation.businessContext ? <p className="text-xs text-destructive">{validation.businessContext}</p> : null}
          </div>

          <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
            <div className="space-y-2">
              <Label>Prioritaet</Label>
              <Select value={priority} onValueChange={(value) => setPriority(value as ClarifyPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value === 'critical' ? 'Kritisch' : value === 'high' ? 'Hoch' : value === 'medium' ? 'Mittel' : 'Niedrig'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative min-w-0 flex-1">
                  <Tag className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addTag();
                      }
                    }}
                    className="pl-9"
                    placeholder="Tag hinzufügen"
                    maxLength={40}
                  />
                </div>
                <Button type="button" variant="outline" onClick={addTag} className="w-full sm:w-auto sm:shrink-0">Hinzufügen</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Verknüpfte Dokumente</Label>
                <p className="text-xs text-muted-foreground">Optional: vorhandene Workspace-Dokumente direkt am Request referenzieren.</p>
              </div>
              {linkedDocumentIds.length > 0 ? (
                <Badge variant="outline">{linkedDocumentIds.length} ausgewählt</Badge>
              ) : null}
            </div>

            <Input
              value={documentSearch}
              onChange={(event) => setDocumentSearch(event.target.value)}
              placeholder="Dokumente filtern"
            />

            <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-border/60 p-2">
              {documentsLoading ? (
                <p className="px-2 py-3 text-sm text-muted-foreground">Dokumente werden geladen…</p>
              ) : filteredDocuments.length === 0 ? (
                <p className="px-2 py-3 text-sm text-muted-foreground">Keine passenden Dokumente gefunden.</p>
              ) : (
                filteredDocuments.map((document) => {
                  const selected = linkedDocumentIds.includes(document.id);
                  return (
                    <button
                      key={document.id}
                      type="button"
                      onClick={() => toggleLinkedDocument(document.id)}
                      className={`flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-secondary/60'}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{document.displayTitle ?? document.fileName}</p>
                        <p className="truncate text-xs text-muted-foreground">{document.fileName}</p>
                      </div>
                      <Badge variant={selected ? 'default' : 'outline'}>{selected ? 'Verknüpft' : 'Auswählen'}</Badge>
                    </button>
                  );
                })
              )}
            </div>
          </div>
          </div>

          <div className="shrink-0 border-t border-border/60 bg-background/95 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              {submitError ? <p className="text-xs text-destructive sm:mr-auto">{submitError}</p> : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Abbrechen</Button>
                <Button type="button" onClick={handleSubmit} disabled={submitting} className="w-full gap-2 sm:w-auto">
                  <Send className="h-4 w-4" />
                  Request einreichen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}